/**
 * Supabase session helpers.
 *
 * Since auth migration phase 5b-2 the Supabase session is the app's
 * credential: stores/authStore signs in with it, services/apiClient sends it
 * as Bearer on every backend request, and hooks/useAuthInit restores the user
 * from it on reload. It is also what Postgres understands — `auth.uid()`
 * resolves from it, so the RLS policies added in migration 000004 scope a
 * direct query to the caller's tenant.
 *
 * The legacy HttpOnly cookie says nothing to Postgres. It is now acquired
 * only by the legacy fallback login (an account whose password Supabase
 * rejects), and ensureSupabaseSession exists for exactly that path: give
 * such a session a Supabase session too, so direct queries work for it as
 * well. Every account has a matching Supabase Auth user (see
 * backend/scripts/backfill_supabase_auth.py), so this usually succeeds; when
 * it does not, that session simply routes every query through the backend.
 */

import { supabase } from '../supabaseClient';
import logger from '../utils/logger';

/**
 * Establish a Supabase session alongside a legacy-fallback login.
 *
 * Never throws — the caller is already authenticated by the time this runs
 * and must not be logged out because data-access setup failed.
 *
 * @returns true when a session is available for direct queries.
 */
export async function ensureSupabaseSession(
  email: string,
  password: string
): Promise<boolean> {
  if (!supabase) return false;

  try {
    const { data: existing } = await supabase.auth.getSession();
    if (existing.session) return true;

    const { error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) {
      // An account that predates the backfill, or a password changed only on
      // the legacy side. Reads keep working through the backend.
      logger.warn('Supabase session unavailable; direct queries disabled', error.message);
      return false;
    }
    return true;
  } catch (error) {
    logger.warn('Supabase session setup failed; direct queries disabled', error);
    return false;
  }
}

/** True when the browser holds a session that RLS can resolve. */
export async function hasSupabaseSession(): Promise<boolean> {
  if (!supabase) return false;
  try {
    const { data } = await supabase.auth.getSession();
    return !!data.session;
  } catch {
    return false;
  }
}

/**
 * Drop the Supabase session. Safe to call when there is none.
 *
 * authStore.logout awaits this before anything that can reach the Render
 * backend (#878): once the user has signed out, a reload must not find the
 * session in localStorage. A plain signOut() is enough for that. supabase-js
 * (auth-js 2.90, `_signOut`) sends the revoke to Supabase Auth first and
 * removes the local copy when that settles, and it does so for every scope —
 * `{ scope: 'local' }` makes the same round-trip, only revoking less
 * server-side — so no scope avoids the network. What makes this safe is that
 * Supabase Auth has no cold start: the call is a sub-second round-trip, not
 * the ~50s that opened the #878 window, and a 401/403/404 from it still
 * removes the local session. The one case that keeps the session is a
 * transport failure (offline); it is logged rather than swallowed, and the
 * store is already cleared by then, so the UI is signed out either way.
 */
export async function clearSupabaseSession(): Promise<void> {
  if (!supabase) return;
  try {
    const { error } = await supabase.auth.signOut();
    if (error) {
      logger.warn('Supabase sign-out did not complete; dropping the local session', error.message);
    }
  } catch (error) {
    logger.warn('Supabase sign-out failed; dropping the local session', error);
  }
  await dropLocalSession();
}

/**
 * Make sure no session survives in this browser whatever the sign-out call
 * did. auth-js only removes the stored session after the server accepted
 * the revoke (or answered 401/403/404); a network failure, a 429 or a
 * session it could not refresh leaves the session in localStorage, and
 * useAuthInit would restore it on the next reload as if nothing happened
 * (#878). The stored session lives under `sb-<project-ref>-auth-token`.
 */
async function dropLocalSession(): Promise<void> {
  if (!supabase) return;
  try {
    const { data } = await supabase.auth.getSession();
    if (!data.session) return;
  } catch {
    // Cannot even read it — fall through and clear the storage regardless.
  }
  const removed = dropStoredSupabaseSession();
  if (removed) logger.warn('Removed a Supabase session that outlived sign-out', removed);
}

/**
 * Remove the stored Supabase session (`sb-<project-ref>-auth-token`) without
 * going through auth-js. Every auth-js call takes the cross-tab lock first,
 * so this is the only safe way to drop a session when that lock is what is
 * hanging (useAuthInit's start-up guard). Returns how many keys were removed.
 */
export function dropStoredSupabaseSession(): number {
  try {
    const keys: string[] = [];
    for (let i = 0; i < localStorage.length; i += 1) {
      const key = localStorage.key(i);
      if (key && /^sb-.*-auth-token/.test(key)) keys.push(key);
    }
    keys.forEach((key) => localStorage.removeItem(key));
    return keys.length;
  } catch {
    // Storage unavailable (private mode, blocked) — nothing persisted anyway.
    return 0;
  }
}
